import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { GET_RECEIPT_DETAIL_WITH_TRANSACTION } from "../../../graphql/receiptDetail";
import { useQuery } from "@apollo/client";
import { formatCashFull } from "../../../utils/formatCash";
import SimpleHoverComponent from "../../common/SimpleHover";
import { Stack } from "components/layout/reusableLayouts";

type LineItemRow = {
  key: string;
  name?: string | null;
  description?: string | null;
  price?: number | null;
  quantity?: number | null;
  productUrl?: string | null;
};

interface LineItemsProps {
  transactionId: string | null;
  refresh?: number;
}

const LineItems: React.FC<LineItemsProps> = (props) => {
  const { transactionId, refresh } = props;
  const { t } = useTranslation();

  const {
    data: receiptDetail,
    loading,
    refetch,
  } = useQuery(GET_RECEIPT_DETAIL_WITH_TRANSACTION, {
    variables: { transactionId },
    skip: !transactionId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (transactionId && refresh) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const rows: LineItemRow[] = useMemo(() => {
    const units: Array<Omit<LineItemRow, "key"> | null> =
      receiptDetail?.receiptDetailWithTransaction?.units ?? [];
    // Units carry no id of their own, and the list re-renders wholesale on
    // refetch, so a positional key is safe.
    return units
      .filter((unit): unit is Omit<LineItemRow, "key"> => !!unit)
      .map((unit, index) => ({ ...unit, key: String(index) }));
  }, [receiptDetail]);

  const columns: DataColumn<LineItemRow>[] = useMemo(
    () => [
      {
        id: "name",
        header: t("Name"),
        maxWidth: 192,
        cell: (item) => (
          <SimpleHoverComponent title={item?.description ?? ""}>
            {item?.productUrl ? (
              <a
                href={item.productUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {item.name}
              </a>
            ) : (
              <span>{item.name}</span>
            )}
          </SimpleHoverComponent>
        ),
      },
      {
        id: "amount",
        header: t("Amount"),
        cell: (item) => (item.price != null ? formatCashFull(item.price) : "-"),
      },
      {
        id: "quantity",
        header: t("Quantity"),
        cell: (item) => item.quantity,
      },
    ],
    [t],
  );

  return (
    <Stack gap="var(--space-2)">
      <Typography variant="caption" component="p">
        {t("LineItemReceiptDisclosure")}
      </Typography>
      <DataTable<LineItemRow>
        columns={columns}
        rows={rows}
        getRowKey={(item) => item.key}
        loading={loading && !receiptDetail}
        emptyState={t("No line items found")}
        stickyHeader
      />
    </Stack>
  );
};

export default LineItems;
